/**
 * MATAFOME — API DE SALÃO, MESAS, COMANDAS E FECHAMENTO DE CONTA
 */

import { safeApiFetch, getApiBase, getAuthHeaders, currentToken } from './core.api';

export async function fetchTables() {
    const res = await safeApiFetch('/api/tables', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar mesas do salão.');
    return data.tables || [];
}

export async function createTable(tableData) {
    const res = await safeApiFetch('/api/tables', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(tableData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao criar mesa.');
    return data.table;
}

export async function deleteTable(tableId) {
    const res = await safeApiFetch(`/api/tables/${tableId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir mesa.');
    return data;
}

export async function clearAllTables() {
    const res = await safeApiFetch('/api/tables/clear-all', {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao limpar mesas livres.');
    return data;
}

export async function openTable(tableId, { customer_name, people_count }) {
    const res = await safeApiFetch(`/api/tables/${tableId}/open`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ customer_name, people_count })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao abrir mesa.');
    return data.table;
}

export async function addTableItems(tableId, items, includeServiceFee = false) {
    const res = await safeApiFetch(`/api/tables/${tableId}/items`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ items, includeServiceFee })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao lançar itens na comanda.');
    return data.table;
}

export async function removeTableItem(tableId, itemIndex) {
    const res = await safeApiFetch(`/api/tables/${tableId}/items/${itemIndex}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao estornar item da comanda.');
    return data.table;
}

export async function updateTableItem(tableId, itemId, itemData) {
    const res = await safeApiFetch(`/api/tables/${tableId}/items/${itemId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(itemData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar item da comanda.');
    return data;
}

export async function requestTableBill(tableId, { serviceFeeIncluded, discount }) {
    const res = await safeApiFetch(`/api/tables/${tableId}/bill`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ serviceFeeIncluded, discount })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao solicitar conta.');
    return data.table;
}

export async function closeTable(tableId, { payment_method, discount, serviceFeeIncluded }) {
    const res = await safeApiFetch(`/api/tables/${tableId}/close`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ payment_method, discount, serviceFeeIncluded })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao fechar conta da mesa.');
    return data;
}

export async function transferTable(sourceId, target_table_id) {
    const res = await safeApiFetch(`/api/tables/${sourceId}/transfer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ target_table_id })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao transferir mesa.');
    return data;
}

export async function recordPartialPayment(tableId, { amount, payment_method, person_name, serviceFeeIncluded, discount }) {
    const res = await safeApiFetch(`/api/tables/${tableId}/partial-payment`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ amount, payment_method, person_name, serviceFeeIncluded, discount })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao registrar pagamento parcial.');
    return data;
}

export async function deletePartialPayment(tableId, paymentId) {
    const res = await safeApiFetch(`/api/tables/${tableId}/partial-payment/${paymentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao estornar pagamento.');
    return data;
}

export async function updateTableItemsAssignment(tableId, { items, split_customers, removed_customer }) {
    const res = await safeApiFetch(`/api/tables/${tableId}/items-assignment`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ items, split_customers, removed_customer })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar divisão de itens.');
    return data;
}

export async function updateTableItemQty(tableId, itemIndex, { delta, newQty }) {
    const res = await safeApiFetch(`/api/tables/${tableId}/items/${itemIndex}/qty`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ delta, newQty })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao alterar quantidade do item.');
    return data.table || data;
}

export function getTableReceiptUrl(tableId, { type = 'kitchen', width = 80, items = null, itemId = null, changeId = null, auto = true } = {}) {
    const token = currentToken || localStorage.getItem('matafome_token') || '';
    let url = `${getApiBase()}/api/tables/${tableId}/receipt?type=${type}&width=${width}&auto=${auto ? 'true' : 'false'}&token=${encodeURIComponent(token)}`;
    if (items && items.length > 0) {
        url += `&items=${encodeURIComponent(JSON.stringify(items))}`;
    }
    if (itemId) {
        url += `&itemId=${encodeURIComponent(itemId)}`;
    }
    if (changeId) {
        url += `&changeId=${encodeURIComponent(changeId)}`;
    }
    return url;
}

// ----------------------------------------------------
// ONBOARDING & CONFIGURAÇÕES DA LOJA
// ----------------------------------------------------
